<script setup>
import { computed } from 'vue';

import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { knowledgeContent } from '../../helpers/kraftchatAIKnowledgeConfig';
import KraftchatAIKnowledgeCard from './KraftchatAIKnowledgeCard.vue';

const props = defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
  meta: {
    type: Object,
    default: () => ({
      page: 1,
      totalCount: 0,
      perPage: 5,
    }),
  },
  // A failed load must never be presented as "no content" - they are
  // different states with different next actions for the user.
  loadError: {
    type: String,
    default: '',
  },
  editingDocumentId: {
    type: Number,
    default: null,
  },
  savingDocumentId: {
    type: Number,
    default: null,
  },
  deletingDocumentId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'edit',
  'update',
  'cancelEdit',
  'delete',
  'pageChange',
  'retry',
]);

const hasError = computed(() => Boolean(props.loadError));

const hasDocuments = computed(() => {
  return props.documents.length > 0;
});

const currentPage = computed(() => props.meta.page || 1);
const totalCount = computed(() => props.meta.totalCount || 0);
const itemsPerPage = computed(() => props.meta.perPage || 5);

const showPagination = computed(() => {
  return totalCount.value > itemsPerPage.value;
});

const handleUpdate = (document, payload) => {
  emit('update', document, payload);
};
</script>

<template>
  <div class="space-y-3">
    <div class="space-y-1">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ knowledgeContent.listTitle }}
      </h3>

      <p class="text-sm leading-6 text-n-slate-11">
        {{ knowledgeContent.listDescription }}
      </p>
    </div>

    <div
      v-if="hasError"
      class="rounded-xl border border-dashed border-n-ruby-6 bg-n-ruby-2 p-4"
    >
      <p class="text-sm font-medium text-n-ruby-11">
        {{ knowledgeContent.errorTitle }}
      </p>

      <p class="mt-1 text-sm leading-6 text-n-ruby-11">
        {{ loadError }}
      </p>

      <NextButton
        type="button"
        size="sm"
        outline
        ruby
        class="mt-3"
        :label="knowledgeContent.errorRetryLabel"
        @click="emit('retry')"
      />
    </div>

    <div
      v-else-if="!hasDocuments"
      class="rounded-xl border border-dashed border-n-weak bg-n-background p-4"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ knowledgeContent.emptyTitle }}
      </p>

      <p class="mt-1 text-sm leading-6 text-n-slate-11">
        {{ knowledgeContent.emptyDescription }}
      </p>
    </div>

    <div v-else class="space-y-3">
      <KraftchatAIKnowledgeCard
        v-for="document in documents"
        :key="document.id"
        :document="document"
        :is-editing="editingDocumentId === document.id"
        :is-saving="savingDocumentId === document.id"
        :is-deleting="deletingDocumentId === document.id"
        @edit="emit('edit', $event)"
        @update="handleUpdate"
        @cancel-edit="emit('cancelEdit')"
        @delete="emit('delete', $event)"
      />

      <div
        v-if="showPagination"
        class="overflow-hidden rounded-xl border border-n-weak"
      >
        <PaginationFooter
          :current-page="currentPage"
          :total-items="totalCount"
          :items-per-page="itemsPerPage"
          @update:current-page="emit('pageChange', $event)"
        />
      </div>
    </div>
  </div>
</template>
